import { useEffect, useState } from 'react';
import { Copy, Key, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, CardBody, CardHeader, Modal } from '@/components/ui';
import { httpClient } from '@/services';
import { showErrorToast } from '@/utils/errorToast';

interface ApiKeyInfo {
  keyId: string;
  name: string | null;
  createdOn: string;
  expiresAt: string | null;
  lastUsedOn: string | null;
}

export function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setIsLoading(true);
      const response = await httpClient.get('/apikeys');
      setApiKeys(response.data || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    setIsGenerating(true);

    try {
      const response = await httpClient.post('/apikeys', {
        name: newKeyName || null,
      });
      const keyValue = response.data?.apiKey;

      if (!keyValue || typeof keyValue !== 'string') {
        throw new Error('Invalid API response: no key found');
      }

      setGeneratedKey(keyValue);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setNewKeyName('');
      await loadApiKeys();
      toast.success('API key created successfully');
    } catch (error) {
      console.error('Failed to create API key:', error);
      showErrorToast('Failed to create API key. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    setDeletingKeyId(keyId);

    try {
      await httpClient.delete(`/apikeys/${keyId}`);
      setApiKeys((current) => current.filter((key) => key.keyId !== keyId));
      toast.success('API key deleted');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    } finally {
      setDeletingKeyId(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('API key copied to clipboard');
  };

  const isExpired = (expiresAt: string | null) =>
    expiresAt ? new Date(expiresAt) < new Date() : false;

  const formatDate = (date: string | null) =>
    date ? new Date(date).toLocaleDateString() : 'Never';

  return (
    <div>
      <PageHeader
        title="API Keys"
        description="Create and revoke API keys for programmatic access"
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'API Keys' }]}
      />

      <div className="max-w-2xl">
        <Card>
          <CardHeader
            title="API Keys"
            description="Keys are shown only once when created"
            icon={<Key className="h-5 w-5" />}
            action={
              <Button
                onClick={() => setShowCreateModal(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Create Key
              </Button>
            }
          />
          <CardBody>
            {isLoading ? (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="py-8 text-center">
                <Key className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-200">
                  No API keys
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Create an API key to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div
                    key={key.keyId}
                    className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-[#2a2a2a] dark:bg-[#0a0a0a]"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-200">
                          {key.name || 'Unnamed Key'}
                        </h4>
                        <Badge variant={isExpired(key.expiresAt) ? 'danger' : 'success'}>
                          {isExpired(key.expiresAt) ? 'Expired' : 'Active'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Created {formatDate(key.createdOn)}
                        {key.expiresAt && ` • Expires ${formatDate(key.expiresAt)}`}
                        {key.lastUsedOn && ` • Last used ${formatDate(key.lastUsedOn)}`}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteKey(key.keyId)}
                      isLoading={deletingKeyId === key.keyId}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewKeyName('');
        }}
        title="Create API Key"
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="api-key-name"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Key Name (optional)
            </label>
            <input
              id="api-key-name"
              type="text"
              value={newKeyName}
              onChange={(event) => setNewKeyName(event.target.value)}
              placeholder="e.g., My CI/CD Key"
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-[#2a2a2a] dark:bg-[#0a0a0a] dark:text-gray-200"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setNewKeyName('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateKey} isLoading={isGenerating}>
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {generatedKey && (
        <Modal
          isOpen={showKeyModal}
          onClose={() => {
            setShowKeyModal(false);
            setGeneratedKey(null);
          }}
          title="API Key Created"
        >
          <div className="space-y-4">
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Important:</strong> Copy this API key now. You won't be able to see it
                again!
              </p>
            </div>
            <div>
              <label
                htmlFor="generated-api-key"
                className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Your new API key
              </label>
              <div className="flex gap-2">
                <input
                  id="generated-api-key"
                  type="text"
                  readOnly
                  value={generatedKey}
                  className="min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 dark:border-[#2a2a2a] dark:bg-[#0a0a0a] dark:text-gray-200"
                />
                <Button
                  variant="secondary"
                  onClick={() => copyToClipboard(generatedKey)}
                  leftIcon={<Copy className="h-4 w-4" />}
                >
                  Copy
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowKeyModal(false);
                  setGeneratedKey(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
